import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import HoldingDailyValuesUploader from "@/components/compliance/HoldingDailyValuesUploader";
import StressScenariosUploader from "@/components/compliance/StressScenariosUploader";
import StressResultsUploader from "@/components/compliance/StressResultsUploader";
import EsgScoresUploader from "@/components/compliance/EsgScoresUploader";
import CounterpartyExposuresUploader from "@/components/compliance/CounterpartyExposuresUploader";
import FxRatesUploader from "@/components/compliance/FxRatesUploader";
import CorporateActionsUploader from "@/components/compliance/CorporateActionsUploader";

const PROFILE_NAMES: Record<number,string> = { 1:"Defensive", 2:"Cautious", 3:"Balanced", 4:"Growth", 5:"Aggressive" };

export default function ComplianceThresholds() {
  const [risk, setRisk] = useState<any[]>([]);
  const [ranges, setRanges] = useState<any[]>([]);
  const [instr, setInstr] = useState<any[]>([]);
  const [taa, setTaa] = useState<any[]>([]);
  const [sectors, setSectors] = useState<any[]>([]);
  const [sectorRet, setSectorRet] = useState<any[]>([]);
  const { toast } = useToast();

  const load = async () => {
    const [r1, r2, r3, r4, r5, r6] = await Promise.all([
      supabase.from("policy_risk_ceilings").select("*").order("profile_level"),
      supabase.from("policy_asset_class_ranges").select("*").order("profile_level").order("asset_class"),
      supabase.from("policy_permitted_instruments").select("*").order("instrument_type"),
      supabase.from("policy_taa_caps").select("*").order("scope"),
      supabase.from("ba_master_sectors").select("id, name, sector_group").order("display_order"),
      supabase.from("policy_sector_index_returns" as any).select("*"),
    ]);
    setRisk(r1.data || []); setRanges(r2.data || []); setInstr(r3.data || []); setTaa(r4.data || []);
    setSectors(r5.data || []); setSectorRet(r6.data || []);
  };
  useEffect(() => { load(); }, []);

  const updateRow = async (table: string, id: string, patch: any) => {
    const { error } = await supabase.from(table as any).update(patch).eq("id", id);
    if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
  };

  const num = (v: any) => v === "" || v == null ? null : Number(v);

  const PERIODS = [
    { days: 21, label: "1m" },
    { days: 63, label: "3m" },
    { days: 126, label: "6m" },
    { days: 252, label: "1y" },
    { days: 756, label: "3y" },
  ];

  const upsertSectorReturn = async (sector_id: string, period_days: number, return_pct: number | null) => {
    if (return_pct == null || Number.isNaN(return_pct)) {
      await supabase.from("policy_sector_index_returns" as any).delete().eq("sector_id", sector_id).eq("period_days", period_days);
    } else {
      const { error } = await supabase.from("policy_sector_index_returns" as any)
        .upsert({ sector_id, period_days, return_pct, as_of_date: new Date().toISOString().slice(0, 10) }, { onConflict: "sector_id,period_days" });
      if (error) toast({ title: "Save failed", description: error.message, variant: "destructive" });
    }
    load();
  };

  const sectorRetMap = new Map<string, number>();
  for (const r of sectorRet) sectorRetMap.set(`${r.sector_id}-${r.period_days}`, Number(r.return_pct));

  return (
    <Tabs defaultValue="risk">
      <TabsList className="flex-wrap h-auto">
        <TabsTrigger value="risk">§11 Risk Ceilings</TabsTrigger>
        <TabsTrigger value="ranges">§6 Asset Ranges</TabsTrigger>
        <TabsTrigger value="instr">§7 Instruments</TabsTrigger>
        <TabsTrigger value="taa">§8 TAA Caps</TabsTrigger>
        <TabsTrigger value="sectorret">§12 Sector Returns</TabsTrigger>
        <TabsTrigger value="holdingdv">§12 Holding Daily Values</TabsTrigger>
        <TabsTrigger value="stress">§13 Stress Scenarios</TabsTrigger>
        <TabsTrigger value="stressres">§13 Stress Results</TabsTrigger>
        <TabsTrigger value="esg">§14 ESG</TabsTrigger>
        <TabsTrigger value="cparty">§15 Counterparties</TabsTrigger>
        <TabsTrigger value="fx">FX Rates</TabsTrigger>
        <TabsTrigger value="corpact">Corp Actions</TabsTrigger>
      </TabsList>

      <TabsContent value="risk">
        <Card><CardHeader><CardTitle>§11 Risk Ceilings per Profile</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm"><thead className="border-b"><tr>
              <th className="text-left p-2">Profile</th><th className="text-left p-2">VaR 95% (%)</th>
              <th className="text-left p-2">Tracking Error (%)</th><th className="text-left p-2">Max Drawdown (%)</th>
              <th className="text-left p-2">Liquidity T+7 min (%)</th></tr></thead>
              <tbody>{risk.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2">{r.profile_level} — {PROFILE_NAMES[r.profile_level]}</td>
                  {(["var95_ceiling_pct","tracking_error_ceiling_pct","max_drawdown_ceiling_pct","liquidity_t7_min_pct"] as const).map((k) => (
                    <td key={k} className="p-2"><Input type="number" defaultValue={r[k]} className="w-24"
                      onBlur={(e) => updateRow("policy_risk_ceilings", r.id, { [k]: num(e.target.value) })} /></td>
                  ))}
                </tr>
              ))}</tbody></table>
          </CardContent></Card>
      </TabsContent>

      <TabsContent value="ranges">
        <Card><CardHeader><CardTitle>§6 Asset Class Ranges</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm"><thead className="border-b"><tr>
              <th className="text-left p-2">Profile</th><th className="text-left p-2">Asset Class</th>
              <th className="text-left p-2">Min %</th><th className="text-left p-2">Max %</th></tr></thead>
              <tbody>{ranges.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2">{r.profile_level} — {PROFILE_NAMES[r.profile_level]}</td>
                  <td className="p-2">{r.asset_class}</td>
                  <td className="p-2"><Input type="number" defaultValue={r.min_pct} className="w-24" onBlur={(e) => updateRow("policy_asset_class_ranges", r.id, { min_pct: num(e.target.value) })} /></td>
                  <td className="p-2"><Input type="number" defaultValue={r.max_pct} className="w-24" onBlur={(e) => updateRow("policy_asset_class_ranges", r.id, { max_pct: num(e.target.value) })} /></td>
                </tr>
              ))}</tbody></table>
          </CardContent></Card>
      </TabsContent>

      <TabsContent value="instr">
        <Card><CardHeader><CardTitle>§7 Permitted Instruments</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm"><thead className="border-b"><tr>
              <th className="text-left p-2">Instrument</th><th className="text-left p-2">Permitted</th>
              <th className="text-left p-2">Conditions</th></tr></thead>
              <tbody>{instr.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2">{r.instrument_type}</td>
                  <td className="p-2"><Switch defaultChecked={r.permitted} onCheckedChange={(v) => updateRow("policy_permitted_instruments", r.id, { permitted: v })} /></td>
                  <td className="p-2"><Input defaultValue={r.conditions ?? ""} onBlur={(e) => updateRow("policy_permitted_instruments", r.id, { conditions: e.target.value })} /></td>
                </tr>
              ))}</tbody></table>
          </CardContent></Card>
      </TabsContent>

      <TabsContent value="taa">
        <Card><CardHeader><CardTitle>§8 TAA Caps</CardTitle></CardHeader>
          <CardContent>
            <table className="w-full text-sm"><thead className="border-b"><tr>
              <th className="text-left p-2">Scope</th><th className="text-left p-2">Cap %</th>
              <th className="text-left p-2">Description</th></tr></thead>
              <tbody>{taa.map((r) => (
                <tr key={r.id} className="border-b">
                  <td className="p-2 font-mono text-xs">{r.scope}</td>
                  <td className="p-2"><Input type="number" defaultValue={r.cap_pct} className="w-24" onBlur={(e) => updateRow("policy_taa_caps", r.id, { cap_pct: num(e.target.value) })} /></td>
                  <td className="p-2 text-muted-foreground">{r.description}</td>
                </tr>
              ))}</tbody></table>
          </CardContent></Card>
      </TabsContent>

      <TabsContent value="sectorret">
        <Card>
          <CardHeader>
            <CardTitle>§12 Sector Index Returns (Brinson Inputs)</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Per-sector benchmark index returns (%) for standard periods. Used by the Performance Attribution panel to compute true Brinson allocation, selection and interaction effects. Leave blank to fall back to the portfolio's total benchmark return.
            </p>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="text-left p-2">Sector</th>
                    {PERIODS.map(p => <th key={p.days} className="text-right p-2">{p.label} (%)</th>)}
                  </tr>
                </thead>
                <tbody>
                  {sectors.map((s: any) => (
                    <tr key={s.id} className="border-b">
                      <td className="p-2 text-xs">{s.name}</td>
                      {PERIODS.map(p => {
                        const v = sectorRetMap.get(`${s.id}-${p.days}`);
                        return (
                          <td key={p.days} className="p-2 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              defaultValue={v ?? ""}
                              className="w-20 ml-auto text-right"
                              onBlur={(e) => upsertSectorReturn(s.id, p.days, e.target.value === "" ? null : Number(e.target.value))}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="holdingdv">
        <HoldingDailyValuesUploader />
      </TabsContent>

      <TabsContent value="stress"><StressScenariosUploader /></TabsContent>
      <TabsContent value="stressres"><StressResultsUploader /></TabsContent>
      <TabsContent value="esg"><EsgScoresUploader /></TabsContent>
      <TabsContent value="cparty"><CounterpartyExposuresUploader /></TabsContent>
      <TabsContent value="fx"><FxRatesUploader /></TabsContent>
      <TabsContent value="corpact"><CorporateActionsUploader /></TabsContent>
    </Tabs>
  );
}
